import React, { useEffect, useMemo } from 'react';

import { Box, Checkbox, Text } from '@rbilabs/universal-components';
import { addressExists } from 'features/store/pages/delivery-store-selector/utils';
import { useIntl } from 'react-intl';

import { useAuthContext } from 'state/auth';
import { useOrderContext } from 'state/order';

const SaveAddressSelection: React.FC = () => {
  const { formatMessage } = useIntl();
  const { user } = useAuthContext();
  const {
    deliveryAddress,
    orderPhoneNumber,
    updateShouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    formShouldSaveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
  } = useOrderContext();

  const isAuthenticated = Boolean(user?.cognitoId && user?.details);

  const { isUsingSavedAddress, isAddressExisting } = useMemo(() => {
    const addresses = user?.details?.deliveryAddresses || [];
    if (!deliveryAddress) {
      return { isUsingSavedAddress: false, isAddressExisting: false };
    }

    const enrichedAddress = {
      ...deliveryAddress,
      phoneNumber: orderPhoneNumber,
    };

    const found = addressExists(addresses, enrichedAddress);
    return {
      allAdress: addresses,
      isUsingSavedAddress: found,
      isAddressExisting: found,
    };
  }, [deliveryAddress, orderPhoneNumber, user?.details?.deliveryAddresses]);

  useEffect(() => {
    if (isUsingSavedAddress) {
      updateShouldSaveDeliveryAddress(false);
      setShouldSaveDeliveryAddress(false);
    }

    if (!isAuthenticated) {
      updateShouldSaveDeliveryAddress(false);
      setShouldSaveDeliveryAddress(false);
      return;
    }

    if (isAuthenticated && !isAddressExisting && formShouldSaveDeliveryAddress) {
      updateShouldSaveDeliveryAddress(true);
      setShouldSaveDeliveryAddress(true);
    }
  }, [
    isUsingSavedAddress,
    isAddressExisting,
    formShouldSaveDeliveryAddress,
    updateShouldSaveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    isAuthenticated,
  ]);

  const handleSaveAddressClick = () => {
    const newValue = !formShouldSaveDeliveryAddress;
    setFormShouldSaveDeliveryAddress(newValue);

    if (isAuthenticated) {
      updateShouldSaveDeliveryAddress(newValue);
      setShouldSaveDeliveryAddress(newValue);
    }
  };

  return (
    <Checkbox
      testID="save-address-selection"
      isChecked={formShouldSaveDeliveryAddress || isAddressExisting}
      onChange={handleSaveAddressClick}
      name="checkbox-save-address-selection"
      value="checkbox-save-address-selection"
      isDisabled={isUsingSavedAddress || isAddressExisting}
    >
      <Box paddingX="$2" flex="1">
        <Text variant="copyTwo">
          {formatMessage(
            { id: 'saveAddressLabel' },
            { disclaimer: formatMessage({ id: 'saveAddressDisclaimer' }) }
          )}
        </Text>
      </Box>
    </Checkbox>
  );
};

export default SaveAddressSelection;
